/** Finite story progression. Inject milliseconds and timers for deterministic lifecycle tests.
 * Pauses retain elapsed time; manual navigation restarts the current frame. No wrap or repeat. */
export function createStoryClock({
  count,
  index = 0,
  duration = 6000,
  now = () => performance.now(),
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = (id) => clearTimeout(id),
  onChange = () => {},
  reduced = false,
}) {
  if (!Number.isInteger(count) || count < 1) throw new Error('Stories required.');
  duration = Number.isFinite(duration) ? Math.max(3000, Math.min(20000, duration)) : 6000;
  index = Number.isInteger(index) ? Math.max(0, Math.min(count - 1, index)) : 0;
  const pauses = new Set(reduced ? ['user'] : []);
  let elapsed = 0,
    last = now(),
    timer,
    disposed = false,
    ended = false;
  function snapshot() {
    return {
      index,
      count,
      progress: Math.min(1, elapsed / duration),
      paused: pauses.size > 0,
      ended,
    };
  }
  function accrue() {
    const time = now();
    if (!pauses.size && !ended) elapsed += Math.max(0, time - last);
    last = time;
  }
  function paint() {
    onChange(snapshot());
    if (!disposed && !pauses.size && !ended) timer = schedule(tick, 100);
  }
  function tick() {
    timer = undefined;
    if (disposed) return;
    accrue();
    if (elapsed >= duration) {
      if (index < count - 1) {
        index++;
        elapsed = 0;
      } else {
        elapsed = duration;
        ended = true;
      }
    }
    paint();
  }
  function stopTimer() {
    if (timer !== undefined) cancel(timer);
    timer = undefined;
  }
  function pause(reason, value) {
    if (disposed) return;
    accrue();
    stopTimer();
    if (value) pauses.add(reason);
    else pauses.delete(reason);
    paint();
  }
  function move(delta) {
    if (disposed) return;
    const target = index + delta;
    if (target < 0 || target >= count) return;
    stopTimer();
    index = target;
    elapsed = 0;
    ended = false;
    last = now();
    paint();
  }
  paint();
  return {
    snapshot,
    pause,
    move,
    dispose() {
      disposed = true;
      stopTimer();
      pauses.clear();
    },
  };
}

/** A single story surface retains its chrome and focus through every frame change. */
export function createStoryViewer(ctx, stories, startId, env = {}) {
  const doc = env.document ?? document;
  const win = env.window ?? window;
  const motion = env.motion ?? win.matchMedia('(prefers-reduced-motion:reduce)');
  const modal = ctx.modal;
  const root = ctx.e('section', 'story-viewer');
  root.setAttribute('aria-label', ctx.uiText('Stories'));
  const progress = ctx.e('div', 'story-viewer-progress');
  progress.setAttribute('aria-hidden', 'true');
  const segments = stories.map(() => {
    const track = ctx.e('span'),
      fill = ctx.e('span');
    track.append(fill);
    progress.append(track);
    return fill;
  });
  const header = ctx.e('div', 'story-viewer-header');
  const author = ctx.link('', '#', 'story-viewer-author');
  const time = ctx.e('span', 'story-viewer-time');
  const byline = ctx.append(ctx.e('div', 'story-viewer-byline'), author, time);
  let clock,
    currentIndex = -1,
    disposed = false,
    opening = true;
  const pause = ctx.iconButton('pause', 'Pause stories', () => {
    const state = clock.snapshot();
    if (state.paused) {
      for (const reason of ['user', 'focus', 'hover']) clock.pause(reason, false);
    } else clock.pause('user', true);
  });
  const close = ctx.iconButton('x', 'Close stories', () => modal.close());
  header.append(byline, pause, close);
  const stage = ctx.e('div', 'story-viewer-stage');
  const image = ctx.field('img', { className: 'story-viewer-image', alt: '' });
  const caption = ctx.e('p', 'story-viewer-caption');
  caption.tabIndex = 0;
  const status = ctx.e('p', 'story-viewer-status');
  status.setAttribute('role', 'status');
  stage.append(image, caption, status);
  const footer = ctx.e('div', 'story-viewer-footer');
  const previous = ctx.iconButton('arrow-left', 'Previous story', () => clock.move(-1));
  const count = ctx.e('span', 'story-viewer-count');
  count.setAttribute('aria-live', 'polite');
  const next = ctx.iconButton('chevron-right', 'Next story', () => clock.move(1));
  footer.append(previous, count, next);
  root.append(progress, header, stage, footer);
  const listeners = [];
  function listen(target, name, action) {
    target.addEventListener(name, action);
    listeners.push(() => target.removeEventListener(name, action));
  }
  function dispose() {
    if (disposed) return;
    disposed = true;
    clock?.dispose();
    for (const remove of listeners) remove();
    modal.classList.remove('story-modal');
  }
  function update(state) {
    if (disposed) return;
    if (state.index !== currentIndex) {
      currentIndex = state.index;
      const story = stories[currentIndex],
        user = ctx.who(story.authorId);
      author.textContent = user.name;
      author.href = `#/profile/${user.id}`;
      author.title = ctx.uiText('View profile');
      time.textContent = ctx.time(story.createdAt);
      caption.textContent = story.text;
      caption.hidden = !story.text;
      status.textContent = ctx.uiText('Loading story');
      image.alt = story.alt || ctx.uiText('Shared photo');
      // Loading is a pause, so delayed media does not consume the reading interval.
      queueMicrotask(() => {
        if (!disposed) clock.pause('media', !image.complete || !image.naturalWidth);
      });
      image.src = ctx.photoSource(story.image);
    }
    previous.setAttribute('aria-disabled', String(state.index === 0));
    next.setAttribute('aria-disabled', String(state.index === stories.length - 1));
    const position = `${state.index + 1} / ${stories.length}`;
    if (count.textContent !== position) count.textContent = position;
    pause.setAttribute('aria-disabled', String(state.ended));
    pause.replaceChildren(ctx.icon(state.paused || state.ended ? 'play' : 'pause'));
    const label = ctx.uiText(
      state.ended ? 'End of stories' : state.paused ? 'Play stories' : 'Pause stories',
    );
    pause.setAttribute('aria-label', label);
    pause.title = label;
    pause.setAttribute('aria-pressed', String(state.paused));
    for (let i = 0; i < segments.length; i++)
      segments[i].style.width =
        `${(i < state.index ? 1 : i === state.index ? state.progress : 0) * 100}%`;
    if (state.ended) status.textContent = ctx.uiText('End of stories');
  }
  listen(image, 'load', () => {
    status.textContent = '';
    clock.pause('media', false);
  });
  listen(image, 'error', () => {
    status.textContent = ctx.uiText('Story unavailable');
    clock.pause('media', true);
  });
  listen(root, 'mouseenter', () => clock.pause('hover', true));
  listen(root, 'mouseleave', () => clock.pause('hover', false));
  listen(root, 'focusin', (event) => {
    if (!opening && event.target !== pause) clock.pause('focus', true);
  });
  listen(root, 'focusout', (event) => {
    if (!root.contains(event.relatedTarget)) clock.pause('focus', false);
  });
  listen(stage, 'pointerdown', () => clock.pause('hold', true));
  listen(win, 'pointerup', () => clock.pause('hold', false));
  listen(win, 'pointercancel', () => clock.pause('hold', false));
  listen(doc, 'visibilitychange', () => clock.pause('hidden', doc.hidden));
  listen(motion, 'change', () => {
    if (motion.matches) clock.pause('user', true);
  });
  listen(root, 'keydown', (event) => {
    if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();
      clock.move(event.key === 'ArrowLeft' ? -1 : 1);
    }
  });
  listen(modal, 'close', dispose);
  listen(win, 'hashchange', () => {
    dispose();
    if (modal.open) modal.close();
  });
  clock = createStoryClock({
    ...env.clock,
    count: stories.length,
    index: Math.max(
      0,
      stories.findIndex((story) => story.id === startId),
    ),
    reduced: motion.matches,
    onChange: update,
  });
  modal.classList.add('story-modal');
  ctx.dialog('Stories', root);
  close.focus({ preventScroll: true });
  opening = false;
  clock.pause('hidden', doc.hidden);
  return { root, clock, dispose };
}
